/**
 * タスク管理（β版）の API hooks (steering 577)
 *
 * ## 既存 `useClientTasks.ts` との違い (取り違え注意)
 * - こちらの BE path は **`/tasks`** (専用 handler `api/tasks.ts`)。依頼者にも案件にも
 *   紐づかない社内タスクを扱える
 * - `useClientTasks.ts` は **`/clients/{clientId}/tasks`** と **`/clients/tasks`** で、
 *   依頼者詳細の「相談タスク」節が今も使っている (受任前相談の文脈)
 * - 更新は **PUT** (ADR-056: HTTP API は PATCH を route しない)
 *
 * ## cursor の扱い
 * `nextCursor` は **opaque**。中身を解釈せず、そのまま次の要求へ返す。
 * 絞り込みを変えたら先頭から取り直す (BE は発行時と違う条件の cursor を 400 で弾く)。
 *
 * ## 空ページ
 * BE は **items が空でも `nextCursor` を返すことがある** (可視性の絞り込みで
 * ページ内が全部落ちた場合)。`null` になるまで追い読みしないと、
 * 「まだ続きがあるのに 0 件」に見える。`useTaskList` がこれを自動で行う。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '@/api/client';
import { useFetch, useMutation } from '@/hooks/useApi';
import { TASKS_CHANGED_EVENT, afterTaskMutation, notifyTasksChanged } from '@/utils/tasksChanged';
import type {
  ClientTask,
  ClientTaskStatus,
  TaskLink,
  TaskListItem,
  TaskListScope,
  TaskPriority,
} from '@/types';

/** 参照 identity を安定させるための空配列 (毎 render の再生成で effect を暴発させない)。 */
const EMPTY_TASK_ITEMS: TaskListItem[] = [];

/**
 * 空ページを追い読みする上限。
 *
 * BE 側にも読み取りページ数の上限があるため、ここは「無限ループにしない」ための
 * 安全弁であって正常系の制約ではない。到達しても表示済みは保持し、
 * `hasMore` を残して利用者が明示的に続けられるようにする。
 */
const EMPTY_PAGE_FOLLOW_MAX = 5;

/** 未担当を表す sentinel (BE と同じ語)。 */
export const TASK_UNASSIGNED = 'unassigned';

export type TaskDueFilter = 'overdue' | 'today' | 'week';

/** 一覧の絞り込み条件。未指定 key は載せない (= 絞らない)。 */
export interface TaskListQuery {
  scope?: TaskListScope;
  /** 省略時 BE 既定は `open`。`all` は `due` と併用できない (下記 §契約)。 */
  status?: ClientTaskStatus | 'all';
  due?: TaskDueFilter;
  priority?: TaskPriority;
  /** userId または `'unassigned'`。 */
  assigneeId?: string;
  creatorId?: string;
  clientId?: string;
  caseId?: string;
  limit?: number;
  /** 前ページの `nextCursor`。**中身は解釈しない** (opaque)。 */
  cursor?: string;
}

export interface TaskListResponse {
  tasks: TaskListItem[];
  nextCursor: string | null;
}

/**
 * 期限で絞り込むとき、状態は「未完了 / 完了」のどちらかでなければならない。
 *
 * BE は `status=all` かつ `due` 指定を 400 で弾く (design §2.6 — 期限は単一 status の
 * 索引レンジ内でしか引けない)。**FE 側で先に潰して 400 を出さない。**
 */
export function isTaskDueStatusConflict(query: Pick<TaskListQuery, 'status' | 'due'>): boolean {
  return Boolean(query.due) && query.status === 'all';
}

/** 自分の範囲と同じ軸の別人指定は空集合。BE の 400 契約に到達させない。 */
export function isTaskScopeFilterEmpty(
  query: Pick<TaskListQuery, 'scope' | 'assigneeId' | 'creatorId'>,
  userId: string | undefined,
): boolean {
  if (!userId) return false;
  const scope = query.scope ?? 'assigned_to_me';
  return Boolean(
    (scope === 'assigned_to_me' && query.assigneeId && query.assigneeId !== userId) ||
    (scope === 'created_by_me' && query.creatorId && query.creatorId !== userId),
  );
}

/** query object を検索文字列へ落とす (未指定 key は載せない)。 */
export function buildTaskListQuery(query: TaskListQuery): string {
  const params = new URLSearchParams();
  if (query.scope) params.set('scope', query.scope);
  if (query.status) params.set('status', query.status);
  if (query.due) params.set('due', query.due);
  if (query.priority) params.set('priority', query.priority);
  if (query.assigneeId) params.set('assigneeId', query.assigneeId);
  if (query.creatorId) params.set('creatorId', query.creatorId);
  if (query.clientId) params.set('clientId', query.clientId);
  if (query.caseId) params.set('caseId', query.caseId);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** タスク一覧の 1 ページ。`enabled=false` なら取得しない。 */
export function useTaskPage(query: TaskListQuery, enabled = true) {
  const path = `/tasks${buildTaskListQuery(query)}`;
  return useFetch<TaskListResponse>(enabled ? path : null);
}

/** 案件詳細「タスク」タブのバッジが読む値 ({@link useCaseOpenTaskBadge})。 */
export interface CaseOpenTaskBadge {
  /** 数えられた未完了タスク (先頭ページ + 空ページの追い読み分) */
  count: number;
  /** 続きがある (実件数は count 以上) */
  more: boolean;
  /** 追い読みしても 1 件も取れず続きだけが残った (または追い読みに失敗した) = 件数を確定できない */
  indeterminate: boolean;
  /** 先頭ページの取得 / 読み直し / 追い読みが進行中 */
  loading: boolean;
  /** 先頭から読み直す (cache を迂回) */
  refetch: () => void;
}

interface CaseOpenTaskBadgeFollow {
  /** どの先頭ページ応答に対する追い読みか (応答 object の identity で結び付ける) */
  source: TaskListResponse;
  tasks: TaskListItem[];
  next: string | undefined;
  failed: boolean;
}

/**
 * 案件詳細「タスク」タブのバッジ用: その案件の未完了タスク件数 (case-tasks-tab-badge-20260916)。
 *
 * - 先頭ページは {@link useTaskPage} (cache 共有)。**空ページ + cursor** は後段フィルタ
 *   (`scope=assigned_to_me` は担当者 GSI を引いてから caseId で絞る) で普通に起こるので、
 *   `EMPTY_PAGE_FOLLOW_MAX` ページまで続きを読み、1 件でも取れた時点で止める。それでも空で続きだけ
 *   残れば `indeterminate` (件数不明) として返し、**正の件数を捏造しない** (Codex review)。
 * - タスクの変更 event ({@link TASKS_CHANGED_EVENT}) で読み直す。取得中に届いた変更は pending に置き、
 *   応答が返ってから改めて読み直す (`fetchThrough` は進行中の要求に相乗りするので、そのままだと
 *   完了操作より前の応答で古い件数が残る — `useMyOpenTaskCount` と同じ方式)。
 * - 表示を始めるとき (mount / 案件切替 / 有効化) は cache が fresh でも 1 回 force で読み直す
 *   (別画面でタスクを完了して 30 秒以内に戻ると、無効化されていない cache が fresh 扱いになるため)。
 */
export function useCaseOpenTaskBadge(
  query: Omit<TaskListQuery, 'cursor'>,
  enabled = true,
): CaseOpenTaskBadge {
  const page = useTaskPage(query, enabled);
  const { refetch: refetchPage, loading, validating } = page;
  const data = enabled ? page.data : null;
  const queryKey = buildTaskListQuery(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  // 表示を始めるとき (有効化 / 条件変更) は 1 回 force で読み直す。初回 (cache 無し) は
  // useFetch 自身の取得へ相乗りするので要求は増えない。
  useEffect(() => {
    if (!enabled) return;
    refetchPage();
  }, [enabled, queryKey, refetchPage]);

  // 変更 event: 進行中なら pending に置き、応答後に読み直す。
  const busy = loading || validating;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const pendingRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const onChanged = () => {
      if (busyRef.current) {
        pendingRef.current = true;
        return;
      }
      refetchPage();
    };
    window.addEventListener(TASKS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
  }, [enabled, refetchPage]);
  useEffect(() => {
    if (busy || !pendingRef.current) return;
    pendingRef.current = false;
    refetchPage();
  }, [busy, refetchPage]);

  // 空ページ + cursor の追い読み。先頭ページの応答 object ごとに 1 回だけ行い、
  // 古い応答に対する結果は使わない (応答が入れ替われば取り直す)。
  const firstTasks = data?.tasks ?? EMPTY_TASK_ITEMS;
  const firstCursor = data?.nextCursor ?? undefined;
  const needFollow = !!data && firstTasks.length === 0 && !!firstCursor;
  const [follow, setFollow] = useState<CaseOpenTaskBadgeFollow | null>(null);
  useEffect(() => {
    // 先頭ページの読み直し中 (再入場の force など) は始めない — 応答で data が入れ替わると同じ cursor を
    // もう一度読むことになる (追い読みは apiFetch 直なので重複排除されない — Codex review)。
    if (!needFollow || !data || !firstCursor || busy) return;
    let active = true;
    void (async () => {
      let next: string | undefined = firstCursor;
      const collected: TaskListItem[] = [];
      let failed = false;
      try {
        for (let i = 0; i < EMPTY_PAGE_FOLLOW_MAX && next; i += 1) {
          const res: TaskListResponse = await apiFetch<TaskListResponse>(
            `/tasks${buildTaskListQuery({ ...queryRef.current, cursor: next })}`,
          );
          if (!active) return;
          collected.push(...(res.tasks ?? []));
          next = res.nextCursor ?? undefined;
          if (collected.length > 0) break;
        }
      } catch {
        // 追い読みの失敗を「0 件」に潰さない (件数不明として扱う)。
        failed = true;
      }
      if (active) setFollow({ source: data, tasks: collected, next, failed });
    })();
    return () => {
      active = false;
    };
  }, [needFollow, data, firstCursor, busy]);

  const followValid = follow !== null && follow.source === data;
  if (!needFollow) {
    return {
      count: firstTasks.length,
      more: !!firstCursor,
      indeterminate: false,
      loading: busy,
      refetch: refetchPage,
    };
  }
  if (!followValid) {
    return { count: 0, more: true, indeterminate: true, loading: true, refetch: refetchPage };
  }
  return {
    count: follow.tasks.length,
    more: !!follow.next || follow.failed,
    indeterminate: follow.tasks.length === 0 && (!!follow.next || follow.failed),
    loading: busy,
    refetch: refetchPage,
  };
}

/**
 * 一覧 + load-more。`useOrgClientTasksList` (steering 504) と同じ形にしてある。
 *
 * 違いは **空ページの自動追い読み**で、BE が空 items + 非 null cursor を返したときに
 * 自動で続きを取る (§2.5 の契約)。これをしないと、可視性で 1 ページ分が全部落ちた
 * だけなのに「0 件」に見えてしまう。
 */
export function useTaskList(query: TaskListQuery, enabled = true) {
  const { data, error, loading, fresh, refetch: refetchPage } = useTaskPage(query, enabled);

  const [extraItems, setExtraItems] = useState<TaskListItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const queryKey = buildTaskListQuery(query);
  // 条件 / 先頭ページ / 有効状態ごとに通信の寿命を分ける。古い callback も無効になる。
  const session = useMemo(() => ({ active: false, inFlight: false }), [queryKey, data, enabled]);

  // 先頭ページが取り直されたら追加分を捨てて cursor を引き直す。
  useEffect(() => {
    session.active = enabled;
    setExtraItems([]);
    setLoadMoreError(null);
    setLoadingMore(false);
    setCursor(enabled ? (data?.nextCursor ?? undefined) : undefined);
    return () => {
      session.active = false;
    };
  }, [data, enabled, session]);

  const refetch = useCallback(() => {
    // 同じ条件の更新でも、先頭ページの応答を待つ間に古い追加分を混ぜない。
    session.active = false;
    setExtraItems([]);
    setCursor(undefined);
    setLoadingMore(false);
    setLoadMoreError(null);
    refetchPage();
  }, [refetchPage, session]);

  const firstPageItems = data?.tasks ?? EMPTY_TASK_ITEMS;
  const tasks = useMemo(
    () => (extraItems.length === 0 ? firstPageItems : [...firstPageItems, ...extraItems]),
    [firstPageItems, extraItems],
  );

  const fetchFrom = useCallback(
    async (from: string) => {
      // 1 回の loadMore で **1 件以上取れるまで** 空ページを追う。
      // 取れた時点で止めるので、通常は 1 往復で終わる。
      let next: string | undefined = from;
      const collected: TaskListItem[] = [];
      for (let i = 0; i < EMPTY_PAGE_FOLLOW_MAX && next; i += 1) {
        if (!session.active) return null;
        const page: TaskListResponse = await apiFetch<TaskListResponse>(
          `/tasks${buildTaskListQuery({ ...query, cursor: next })}`,
        );
        if (!session.active) return null;
        collected.push(...(page.tasks ?? []));
        next = page.nextCursor ?? undefined;
        if (collected.length > 0) break;
      }
      return { collected, next };
    },
    [query, session],
  );

  const loadMore = useCallback(() => {
    if (!enabled || loading || error || !cursor || !session.active || session.inFlight) return;
    session.inFlight = true;
    setLoadingMore(true);
    setLoadMoreError(null);
    void (async () => {
      try {
        const result = await fetchFrom(cursor);
        if (!session.active || !result) return;
        const { collected, next } = result;
        // 追加のみ行うため、失敗しても既取得分は消えない。
        setExtraItems((prev) => [...prev, ...collected]);
        setCursor(next);
      } catch (e) {
        if (!session.active) return;
        // **取得失敗を空状態へ潰さない。** 表示済みは維持し、再試行できるようにする。
        setLoadMoreError(e instanceof Error ? e.message : '追加の取得に失敗しました');
      } finally {
        session.inFlight = false;
        if (session.active) setLoadingMore(false);
      }
    })();
  }, [cursor, fetchFrom, enabled, loading, error, session]);

  return {
    tasks,
    error,
    loading,
    refetch,
    hasMore: Boolean(cursor),
    loadingMore,
    loadMoreError,
    loadMore,
    /**
     * 先頭ページがこの条件の最新か (`useFetch` の `fresh`)。期限切れの cache を表示している間は false。
     * my-assigned-default-view-20261005: 「自分の担当が 0 件なら全体で始める」を古い結果で判定しないため。
     */
    fresh,
  };
}

/** 単体取得。旧方式のタスクは依頼者配下の既存 API から読む。 */
export function useTask(taskId: string | null, legacyClientId: string | null = null) {
  const path = taskId
    ? legacyClientId
      ? `/clients/${encodeURIComponent(legacyClientId)}/tasks/${encodeURIComponent(taskId)}`
      : `/tasks/${encodeURIComponent(taskId)}`
    : null;
  const result = useFetch<ClientTask>(path);
  // URL 切替直後の古い応答を、新しく選んだタスクとして描画しない。
  const data =
    result.data?.taskId === taskId && (!legacyClientId || result.data.clientId === legacyClientId)
      ? result.data
      : null;
  return { ...result, data };
}

export interface CreateTaskInput {
  requestId?: string;
  chatwork?: import('@/types').TaskChatworkInput;
  /** 対応状況。欠落は actionable。完了状態・提出期限とは独立。 */
  workState?: 'actionable' | 'waiting';
  /** 次回確認日 (JST 暦日)。欠落/null は指定なし。 */
  followUpOn?: string | null;
  /** 次回確認のメモ。履歴・監査ログへ本文を複製しない。 */
  followUpNote?: string | null;

  title: string;
  description?: string | null;
  priority?: TaskPriority;
  /**
   * 期限。`dueAt` (時点) と `dueDate` (暦日) は **相互排他**で、片方だけを送る。
   * 両方送ると BE が 400 を返す。
   */
  dueDate?: string | null;
  dueAt?: string | null;
  assigneeId?: string | null;
  clientId?: string | null;
  caseId?: string | null;
  /** 関連 (steering 611)。省略時は関連なし。 */
  links?: TaskLink[];
}

/** 作成。常に未完了で作られる。担当者を指定すると本人へ in-app 通知が飛ぶ。 */
export function useCreateTask() {
  const fn = useCallback(
    (body: CreateTaskInput) =>
      apiFetch<ClientTask>('/tasks', { method: 'POST', body: JSON.stringify(body) }).then(
        afterTaskMutation,
      ),
    [],
  );
  return useMutation<CreateTaskInput, ClientTask>(fn);
}

export interface UpdateTaskInput {
  expectedVersion?: number;
  /** 対応状況。欠落は actionable。完了状態・提出期限とは独立。 */
  workState?: 'actionable' | 'waiting';
  /** 次回確認日 (JST 暦日)。欠落/null は指定なし。 */
  followUpOn?: string | null;
  /** 次回確認のメモ。履歴・監査ログへ本文を複製しない。 */
  followUpNote?: string | null;

  taskId: string;
  title?: string;
  description?: string | null;
  priority?: TaskPriority;
  dueDate?: string | null;
  dueAt?: string | null;
  clientId?: string | null;
  caseId?: string | null;
  /** 関連 (steering 611)。**外した結果の全体**を送る (画面側は編集のたびに現在の一覧を丸ごと送る)。 */
  links?: TaskLink[];
}

/**
 * 編集 (部分更新)。**担当者はここでは変えない** — 引き継ぎは専用の
 * `useChangeTaskAssignee` を使う (通知と監査を担当変更として残すため)。
 */
export function useUpdateTask() {
  const fn = useCallback(
    ({ taskId, ...body }: UpdateTaskInput) =>
      apiFetch<ClientTask>(`/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify(body) }).then(
        afterTaskMutation,
      ),
    [],
  );
  return useMutation<UpdateTaskInput, ClientTask>(fn);
}

export interface TaskActionInput {
  expectedVersion?: number;
  completionNote?: string;
  taskId: string;
}

/** 完了。既に完了なら BE 側で冪等 no-op になる。 */
export function useCompleteTask() {
  const fn = useCallback(
    ({ taskId, ...body }: TaskActionInput) =>
      apiFetch<ClientTask>(`/tasks/${taskId}/complete`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then(afterTaskMutation),
    [],
  );
  return useMutation<TaskActionInput, ClientTask>(fn);
}

/** 再開。既に未完了なら BE 側で冪等 no-op になる。 */
export function useReopenTask() {
  const fn = useCallback(
    ({ taskId }: TaskActionInput) =>
      apiFetch<ClientTask>(`/tasks/${taskId}/reopen`, { method: 'POST' }).then(afterTaskMutation),
    [],
  );
  return useMutation<TaskActionInput, ClientTask>(fn);
}

export interface ChangeTaskAssigneeInput {
  taskId: string;
  /** `null` で未担当に戻す。 */
  assigneeId: string | null;
}

/** 担当の変更・引き継ぎ。新しい担当者へ in-app 通知が飛ぶ。 */
export function useChangeTaskAssignee() {
  const fn = useCallback(
    ({ taskId, assigneeId }: ChangeTaskAssigneeInput) =>
      apiFetch<ClientTask>(`/tasks/${taskId}/assignee`, {
        method: 'POST',
        body: JSON.stringify({ assigneeId }),
      }).then(afterTaskMutation),
    [],
  );
  return useMutation<ChangeTaskAssigneeInput, ClientTask>(fn);
}

/**
 * 削除。
 *
 * **DELETE は 204 (本文なし) だが、成功時に `true` を返す。**
 * `useMutation` は失敗時に `null` を返すため、`apiFetch<void>` の戻り値
 * (`undefined`) をそのまま流すと成功と失敗がどちらも falsy になり区別できない
 * (`useDeleteClientTask` と同じ理由)。
 */
export function useDeleteTask() {
  const fn = useCallback(async ({ taskId }: TaskActionInput) => {
    await apiFetch<void>(`/tasks/${taskId}`, { method: 'DELETE' });
    notifyTasksChanged();
    return true as const;
  }, []);
  return useMutation<TaskActionInput, true>(fn);
}
